import React from "react";
import {
  Box, Chip, CircularProgress, List, ListItem, ListItemText, Switch, Typography,
} from "@mui/material";
import TvIcon from "@mui/icons-material/Tv";
import {
  useGetDevicesQuery, useAssignDevicePlaylistGroupMutation,
} from "../../../Api/attoDeskApi";
import { TabCard, NeedsGroup } from "./menuTabsUi";
import { useNotifier } from "../../../Core/Notifier";
import { SUCCESS, TEXT_SECONDARY } from "../../../theme/tokens";
import { MenuTabProps } from "./MenuTabs";

/**
 * Assign / unassign devices to this menu's playlist group. Reuses the
 * existing device → group assignment endpoint.
 */
const ScreenAssignmentTab: React.FC<MenuTabProps> = ({ groupId, companyId }) => {
  const { showMessage, showErrorMessage } = useNotifier();
  const { data, isLoading } = useGetDevicesQuery(
    companyId ? { company_id: companyId } : undefined
  );
  const [assign, { isLoading: saving }] = useAssignDevicePlaylistGroupMutation();

  if (!groupId) return <NeedsGroup what="Screen assignment" />;

  const devices: any[] = (data?.data as any[]) ?? [];

  const toggle = async (device: any, on: boolean) => {
    try {
      await assign({ id: device.id, playlist_group_id: on ? groupId : null }).unwrap();
      showMessage(on ? `Assigned ${device.device_name}` : `Unassigned ${device.device_name}`);
    } catch {
      showErrorMessage("Could not update the device assignment.");
    }
  };

  return (
    <TabCard title="Screen Assignment" subtitle="Choose which screens play this menu schedule"
      icon={<TvIcon fontSize="small" />}>
      {isLoading ? (
        <Box sx={{ textAlign: "center", py: 4 }}><CircularProgress size={26} /></Box>
      ) : devices.length === 0 ? (
        <Typography sx={{ color: TEXT_SECONDARY, textAlign: "center", py: 4 }}>
          No devices found for this company.
        </Typography>
      ) : (
        <List disablePadding>
          {devices.map((d) => {
            const assigned = Number(d.playlist_group_id) === groupId;
            return (
              <ListItem key={d.id} divider
                secondaryAction={
                  <Switch checked={assigned} disabled={saving}
                    onChange={(e) => toggle(d, e.target.checked)} />
                }>
                <ListItemText
                  primary={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography sx={{ fontWeight: 700 }}>{d.device_name}</Typography>
                      <Chip size="small" label={d.device_code}
                        sx={{ fontSize: 10, height: 18 }} />
                      {assigned && <Chip size="small" label="On this menu"
                        sx={{ fontSize: 10, height: 18, color: "#fff", bgcolor: SUCCESS }} />}
                    </Box>
                  }
                  secondary={d.location || undefined}
                />
              </ListItem>
            );
          })}
        </List>
      )}
    </TabCard>
  );
};

export default ScreenAssignmentTab;
